import { Asset, Button, CheckmarkIcon } from '@clearcover/atlas';
import cx from 'classnames';
import React, { useMemo } from 'react';

import classNames from 'classnames';
import { ExpansionPanel } from 'Shared/components';
import { FeatureLine } from './FeatureLine';
import { FeeLine } from './FeeLine';
import styles from './styles.module.css';
import { FeeCharge, FeeStrategy } from './types';

export type FeeStrategyCardProps = {
  strategy: FeeStrategy;
  selected: boolean;
  allFees: {
    name: string;
    displayName: string;
    maxCharge: FeeCharge;
  }[];
  allFeatures: string[];
  onSelectStrategy?: () => void;
  disabled?: boolean;
};

const generateDataTestId = (label: string) =>
  `fee-strategy-${label.toLowerCase().replace(/\s+/g, '-')}`;

export const FeeStrategyCard: React.FC<FeeStrategyCardProps> = ({
  strategy,
  selected,
  allFees,
  allFeatures,
  onSelectStrategy,
  disabled,
  ...props
}) => {
  const dataTestId = generateDataTestId(strategy.name);
  const fees = useMemo(() => {
    return allFees.map(({ name, displayName, maxCharge }) => {
      const fee = strategy.fees.find((fee) => fee.name === name);
      return {
        displayName,
        charge: fee?.charge ?? { type: 'FLAT', amount: 0 },
        maxCharge,
      };
    });
  }, [allFees, strategy.fees]);

  const features = useMemo(() => {
    return allFeatures.map((identifier) => {
      const feature = strategy.features.find(
        (feature) => feature.identifier === identifier
      );
      return (
        feature || {
          identifier,
          enabled: false,
        }
      );
    });
  }, [allFeatures, strategy.features]);

  return (
    <div
      className={cx(styles.strategyCardContainer, {
        [styles.selected]: selected,
      })}
      {...props}
      data-testid={dataTestId}
    >
      <ExpansionPanel
        className=""
        head={
          <div className={styles.strategyCardHeader}>
            <h4 data-testid={`${dataTestId}-title`}>{strategy.title}</h4>
            {selected ? (
              <Button
                variant="primary"
                size="sm"
                data-testid={`${dataTestId}-selected-button`}
                className={classNames(
                  styles.selectedButton,
                  styles.strategySelectButton
                )}
                disabled={disabled}
              >
                <span>
                  <Asset
                    icon={CheckmarkIcon}
                    height={16}
                    width={16}
                    description="Checkmark icon"
                  />
                  Selected
                </span>
              </Button>
            ) : (
              <Button
                variant="secondary"
                className={styles.strategySelectButton}
                size="sm"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  onSelectStrategy?.();
                  e.stopPropagation();
                }}
                data-testid={`${dataTestId}-select-button`}
                disabled={disabled}
              >
                Switch to {strategy.title}
              </Button>
            )}
          </div>
        }
      >
        <div className={styles.sectionContent}>
          <div className={styles.feeLineGroup}>
            {fees.map((fee, feeIndex) => (
              <FeeLine key={`${strategy.name}-${feeIndex}`} fee={fee} />
            ))}
            {features.map((feature, featureIndex) => (
              <FeatureLine
                key={`${strategy.name}-feature-${featureIndex}`}
                feature={feature}
              />
            ))}
          </div>
        </div>
      </ExpansionPanel>
    </div>
  );
};
